import { ButtonGroup, HStack, Button, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { naivePluralize } from '../common/helpers/utils';
import {
    SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX,
    SEARCH_RESULTS_FOOTER_HEIGHT_PX,
} from '../search-windows/constants';
import { ResultSelectionStore } from './use-observable-search-results-selection-store';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { FileExportHandlerType, FileExportType } from '../event-search/types';

export const SearchResultsExportsFooter = observer(function SearchResultsExportsFooter({
    selection,
    totalCount,
    onFileExport,
    isExportLoading,
    isCSVExportLoading,
    isEventSearch,
    allowConsolidatedExport,
}: {
    selection: ResultSelectionStore;
    totalCount: number;
    onFileExport?: FileExportHandlerType;
    isExportLoading: boolean;
    isCSVExportLoading?: boolean;
    allowConsolidatedExport?: boolean;
    isEventSearch?: boolean;
}) {
    const selectedIds = Object.keys(selection.selectedItems).map((id) => parseInt(id));
    const enabledFeatures = useEnabledFeatures();

    const isPDFExportEnabled = enabledFeatures.enhancedSearchExportsEnabled && isEventSearch;
    const handleExport = useCallback(
        (fileType?: FileExportType) => {
            onFileExport?.({
                type: fileType,
                ...(selection.allSelected ? {} : { entityIds: selectedIds }),
            });
        },
        [onFileExport, selection.allSelected, selectedIds]
    );

    const selectedCount = selection.allSelected ? totalCount : selectedIds.length;
    return (
        <HStack
            justifyContent="space-between"
            w="100%"
            h={`${SEARCH_RESULTS_EXPORTS_FOOTER_HEIGHT_PX}px`}
            position="absolute"
            bottom={`${SEARCH_RESULTS_FOOTER_HEIGHT_PX}px`}
            background="var(--arc-colors-surface-foreground)"
            padding="0px 16px"
            borderTop="1px solid var(--arc-colors-border-default)"
        >
            {selection.allSelected ? (
                <Text>All {totalCount.toLocaleString()} items are selected.</Text>
            ) : (
                <SomeSelectedMessage
                    selectedCount={selectedIds.length}
                    totalCount={totalCount}
                    selectAll={selection.selectAll}
                />
            )}
            <HStack>
                <ButtonGroup>
                    <Button variant="ghost" onClick={() => selection.unselectAll()}>
                        Clear Selection
                    </Button>
                    {allowConsolidatedExport && (
                        <Button
                            variant="solid"
                            onClick={() => handleExport(FileExportType.CSV)}
                            isLoading={isCSVExportLoading}
                        >
                            Export CSV
                        </Button>
                    )}
                    <Button
                        variant="solid"
                        leadingVisual="Export"
                        onClick={() =>
                            handleExport(isPDFExportEnabled ? FileExportType.PDF : undefined)
                        }
                        isLoading={isExportLoading}
                    >
                        Export{' '}
                        {isPDFExportEnabled
                            ? 'PDF'
                            : `${selectedCount.toLocaleString()}  ${naivePluralize(
                                  'Result',
                                  selectedCount
                              )}`}
                    </Button>
                </ButtonGroup>
            </HStack>
        </HStack>
    );
});

function SomeSelectedMessage({
    selectedCount,
    totalCount,
    selectAll,
}: {
    selectedCount: number;
    totalCount: number;
    selectAll: () => void;
}) {
    const isPlural = selectedCount > 1;
    return (
        <HStack>
            <Text fontWeight="bold">{selectedCount}</Text>
            {isPlural ? (
                <Text>items on this page are selected.</Text>
            ) : (
                <Text>item on this page is selected.</Text>
            )}
            <Button variant="ghost" onClick={() => selectAll()}>
                Select all {totalCount.toLocaleString()} items
            </Button>
        </HStack>
    );
}
